import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { APIResponse, QueryReturnValue } from '~/api/baseFetch/common.type';
import { useCommon } from '~/utils/hook/useCommon';
import { routePath } from '~/utils/constants';
import { setShowAdminReloginDialog } from '~/store/user';

// eslint-disable-next-line react-hooks/rules-of-hooks
const { getToken, updateTokenJwt } = useCommon();

const TOKEN_INVALID_ERR_CODE = 2007;

const NEW_TOKEN_HEADER = 'x-new-token';

const hasTokenInvalidBody = (body: unknown): boolean => {
  if (!body || typeof body !== 'object') return false;
  const payload = body as { err?: unknown; data?: unknown };
  if (payload.err === TOKEN_INVALID_ERR_CODE) return true;
  const inner = payload.data;
  if (
    inner &&
    typeof inner === 'object' &&
    (inner as { httpCode?: unknown }).httpCode === 401
  ) {
    return true;
  }
  return false;
};

const isTokenInvalidResult = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
): boolean => {
  if (result.error) {
    const { status, data } = result.error as FetchBaseQueryError & {
      data?: unknown;
    };
    if (status === 401) return true;
    return hasTokenInvalidBody(data);
  }
  return hasTokenInvalidBody(result.data);
};

const isAdminAreaPath = (pathname: string): boolean =>
  pathname.startsWith(routePath.admin) ||
  pathname.startsWith(routePath.latestNews);

let hasPromptedAdminRelogin = false;

const shouldPromptAdminRelogin = (): boolean => {
  if (typeof window === 'undefined' || hasPromptedAdminRelogin) return false;
  const { pathname } = window.location;
  if (!isAdminAreaPath(pathname) || pathname === routePath.adminLogin) {
    return false;
  }
  hasPromptedAdminRelogin = true;
  return true;
};

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_ONEBOX3_API_DOMAIN,
  method: 'POST',
  prepareHeaders: headers => {
    const token = getToken();
    if (token) {
      headers.set('token', token);
    }
    return headers;
  },
});

export const oneBoxV3FetchBase: BaseQueryFn = async (
  args: string | FetchArgs,
  api,
  extraOptions,
) => {
  const result = (await baseQuery(args, api, extraOptions)) as QueryReturnValue<
    APIResponse<unknown>,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  >;
  const newToken = result.meta?.response?.headers.get(NEW_TOKEN_HEADER);
  if (newToken) {
    updateTokenJwt(newToken);
  }
  if (isTokenInvalidResult(result) && shouldPromptAdminRelogin()) {
    api.dispatch(setShowAdminReloginDialog(true));
  }
  return result;
};
